import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { ChannelNameTokens, Rental, Toaster } from '@tokeet-frontend/tv3-platform'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import { EMPTY, Observable, of, timer } from 'rxjs'
import {
  AirBnBBookingPrefResponse,
  AirBnBPriceSettingsResponse,
  AirBnBUpdateBookingPrefPayload,
  AirBnBUpdatePriceSettingsPayload,
} from '@tv3/store/connection/connection.types'
import { catchError, map, reduce, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators'
import * as lodash from 'lodash'
import { HttpGetRequestCacheService } from '@tokeet-frontend/tv3-platform'
import { ChannelImportStatus, isTerminalImportStatus } from '@tv3/store/connection/connection.model'

export interface AibrnbListingDetails extends Omit<Rental, 'status'> {
  // Airbnb's listing_approval_status passed through by GET /abbv2/listing/detail: a hash
  // keyed approval_status_category, or the literal string 'Unknown' when Airbnb sent none.
  status?: { approval_status_category?: string; status_category?: string } | string
  pets_allowed: 0 | 1
}

export interface ChannelImportStatusResponse {
  channelId: string
  propertyId: string | number
  import_status?: ChannelImportStatus
  import_error?: string
  import_error_time?: number
}

@Injectable({
  providedIn: 'root',
})
export class ConnectionAirbnbService {
  constructor(private http: HttpClient, private toaster: Toaster, private httpCache: HttpGetRequestCacheService) {}

  getPropertyStatus(propertyId: string | number): Observable<any> {
    const url = `@api/abbv2/publish/status`
    return this.http.post(url, { propertyId })
  }

  getVisibilityStatusV2(
    channelId: string,
    propertyId: string | number,
    roomId: string | number
  ): Observable<{ visible: boolean }> {
    const url = `@api/abbv2/listing/visibility/${channelId}?property=${propertyId}&room=${roomId}`
    return this.http.get<{ visible: boolean }>(url).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  setVisibilityStatusV2(
    channelId: string,
    propertyId: string | number,
    roomId: string | number,
    visible: boolean,
    payload?: any
  ) {
    const url = `@api/abbv2/listing/visibility/${channelId}?property=${propertyId}&room=${roomId}&visible=${visible}`
    return this.http.put(url, payload || {}).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  prepareTestListingV2(channelId: string, propertyId: string | number, roomId: string | number) {
    const url = `@api/abbv2/listing/prepare_test/${channelId}?property=${propertyId}&room=${roomId}`
    return this.http.put(url, {}).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  importRoom(channelName: ChannelNameTokens, channelId: string, roomId: string | number, propertyId: string | number) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(
      channelName
    )}/save/room/${roomId}/${channelId}?save=1&property=${propertyId}`
    return this.http.get(url).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  // Queues one listing-import job for the connected Airbnb account. Omit listingIds to
  // import every active listing on the account. A single job (rather than one message per
  // listing) is what makes the recorded import status meaningful: total/imported describe
  // the whole batch.
  importListings(channelId: string, propertyId: string | number, listingIds?: (string | number)[]) {
    const url = `@api/abbv2/save/rooms/${channelId}`
    const property: { id: string | number; listings?: (string | number)[] } = { id: propertyId }
    if (listingIds?.length) {
      property.listings = listingIds
    }
    return this.http.put(url, { properties: [property] })
  }

  getImportStatus(channelId: string): Observable<ChannelImportStatusResponse> {
    const url = `@api/abbv2/import/status/${channelId}`
    return this.http.get<ChannelImportStatusResponse>(url)
  }

  // Emits the recorded listing-import job status every intervalMs until the job turns
  // terminal (done/failed), no job is on record after a few polls, or timeoutMs passes.
  // Emits null while/when no job is recorded (nothing queued yet, or an older backend).
  pollImportStatus(channelId: string, intervalMs = 3000, timeoutMs = 10 * 60 * 1000) {
    let misses = 0
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getImportStatus(channelId).pipe(catchError(() => of(null)))),
      map((res) => res?.import_status || null),
      tap((status) => (misses = status ? 0 : misses + 1)),
      takeUntil(timer(timeoutMs)),
      takeWhile((status) => !isTerminalImportStatus(status) && misses < 3, true)
    )
  }

  // Resolves once with the job's terminal status, or with null when the outcome is
  // unknowable: no job on record, or the job still running when timeoutMs passes.
  waitForImportResult(channelId: string, intervalMs = 3000, timeoutMs = 10 * 60 * 1000) {
    return this.pollImportStatus(channelId, intervalMs, timeoutMs).pipe(
      reduce((_, status) => status, null as ChannelImportStatus | null),
      map((status) => (isTerminalImportStatus(status) ? status : null))
    )
  }

  getBookingRules(
    channelName: ChannelNameTokens,
    channelId: string,
    propertyId: number,
    roomId: number
  ): Observable<AirBnBBookingPrefResponse> {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(
      channelName
    )}/booking/rules/${channelId}?property=${propertyId}&room=${roomId}`
    return this.http.get<any>(url).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  updateBookingRules(channelName: ChannelNameTokens, payload: AirBnBUpdateBookingPrefPayload) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/booking/rules`
    return this.http.put(url, payload).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  getPriceSettingsV2(channelId: string, propertyId: number, roomId: number): Observable<AirBnBPriceSettingsResponse> {
    const url = `@api/abbv2/price/settings/${channelId}?property=${propertyId}&room=${roomId}`
    return this.http.get<any>(url).pipe(
      map((res) => (lodash.isArray(res) ? res[0] : res)),
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  updatePriceSettingsV2(payload: AirBnBUpdatePriceSettingsPayload) {
    const url = `@api/abbv2/price/settings`
    return this.http.put(url, payload).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  syncImagesV2(channelId: string, propertyId: number, roomId: number) {
    const url = `@api/abbv2/listing/images/${channelId}?property=${propertyId}&room=${roomId}`
    return this.http.put(url, {}).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  deleteListingV2(channelId: string, propertyId: number, roomId: number) {
    const url = `@api/abbv2/listing/${channelId}?property=${propertyId}&room=${roomId}`
    return this.http.delete(url).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  getListingDetailsV2(propertyId: any, roomId: any) {
    const url = `@api/abbv2/listing/detail?listing=${roomId}&property=${propertyId}`
    // TTL rather than the default indefinite cache: the listing approval status this
    // feeds (GetAirbnbListingStatus) changes as Airbnb reviews the listing, so a page
    // revisit should be able to observe the new state within the same SPA session.
    return this.httpCache.cacheGetRequest<AibrnbListingDetails>(url, { ttl: 5 * 60 * 1000 })
  }

  updateListingDetailsV2(channelId: string, propertyId: number, roomId: number) {
    const url = `@api/abbv2/listing/details/${channelId}?property=${propertyId}&room=${roomId}`
    return this.http.put(url, {}).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }

  pushRoomsV2(propertyId: string | number, rentalId: string, listingId: string) {
    const url = `@api/abbv2/listing/room/sync`

    return this.http
      .post(url, {
        property: propertyId,
        listing: listingId,
        rental: rentalId,
      })
      .pipe(
        catchError((error) => {
          this.toaster.error(null, $localize`Error`, error)
          return EMPTY
        })
      )
  }

  uploadTransactions(payload: {
    transactions: any[]
    rentalid: string
    sendto: string
  }): Observable<{ total: number; created: number; updated: number }> {
    const url = `@api/channel/airbnb/upload`
    return this.http.post<any>(url, payload).pipe(
      catchError((error) => {
        this.toaster.error(null, $localize`Error`, error)
        return EMPTY
      })
    )
  }
}
